import React from "react";
import "./App.css";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { BrandingProvider } from "@/lib/branding";
import { Toaster } from "@/components/ui/sonner";
import { AiJobsProvider } from "@/lib/ai-jobs";
import AiJobsTray from "@/components/AiJobsTray";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import AppShell from "@/components/AppShell";
import ProtectedRoute from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import ProgramDetail from "@/pages/ProgramDetail";
import Apply from "@/pages/Apply";
import HealthCareAssistant from "@/pages/HealthCareAssistant";
import RoleLoginPage from "@/pages/auth/RoleLoginPage";
import TwoFactorSetup from "@/pages/auth/TwoFactorSetup";
import TwoFactorVerify from "@/pages/auth/TwoFactorVerify";
import Register from "@/pages/Register";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Certificate from "@/pages/Certificate";
import VerifyCertificate from "@/pages/Verify";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import StudentDashboard from "@/pages/student/Dashboard";
import Learn from "@/pages/student/Learn";
import MyAttendance from "@/pages/student/Attendance";
import AccountProfile from "@/pages/AccountProfile";

import InstructorDashboard from "@/pages/instructor/Dashboard";
import InstructorProgramEditor from "@/pages/instructor/ProgramEditor";
import InstructorInbox from "@/pages/instructor/Inbox";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminUsers from "@/pages/admin/Users";
import AdminPrograms from "@/pages/admin/Programs";
import AdminBranding from "@/pages/admin/Branding";
import AdminAttendance from "@/pages/admin/Attendance";
import AdminStudents from "@/pages/admin/Students";
import AdminStudentDetail from "@/pages/admin/StudentDetail";
import AdminStudentAttendance from "@/pages/admin/StudentAttendance";
import AdminIntegrity from "@/pages/admin/Integrity";
import AdminInstructors from "@/pages/admin/Instructors";
import AdminAccuplacer from "@/pages/admin/Accuplacer";
import AdminApplications from "@/pages/admin/Applications";
import AdminPendingStudents from "@/pages/admin/PendingStudents";
import AdminProgramSchedules from "@/pages/admin/ProgramSchedules";
import AdminAssignmentGrading from "@/pages/admin/AssignmentGrading";
import AccuplacerLanding from "@/pages/accuplacer/Landing";
import AccuplacerRegister from "@/pages/accuplacer/Register";
import AccuplacerTestLogin from "@/pages/accuplacer/TestLogin";
import AccuplacerRunner from "@/pages/accuplacer/Runner";
import AccuplacerTestWindowOpen from "@/pages/accuplacer/TestWindowOpen";
import InstructorStudents from "@/pages/instructor/Students";
import InstructorStudentDetail from "@/pages/instructor/StudentDetail";
import SMEDashboard from "@/pages/sme/Dashboard";

const INTERNAL_PREFIXES = [
    "/dashboard",
    "/learn",
    "/certificate",
    "/me",
    "/account",
    "/instructor",
    "/admin",
];

function RootLayout() {
    const { user } = useAuth();
    const location = useLocation();
    const isInternalRoute = INTERNAL_PREFIXES.some((p) => location.pathname.startsWith(p));
    const showShell = !!user && isInternalRoute;

    if (showShell) {
        return (
            <AppShell>
                <Outlet />
            </AppShell>
        );
    }

    return (
        <>
            <ImpersonationBanner />
            <Header />
            <main className="min-h-[60vh]">
                <Outlet />
            </main>
            <Footer />
        </>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrandingProvider>
                    <AiJobsProvider>
                        <BrowserRouter>
                        <Routes>
                            <Route element={<RootLayout />}>
                                <Route path="/" element={<Landing />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/contact" element={<Contact />} />
                                <Route path="/programs/:id" element={<ProgramDetail />} />
                                <Route path="/apply" element={<Apply />} />
                                <Route path="/health-care-assistant" element={<HealthCareAssistant />} />
                                <Route path="/login" element={<RoleLoginPage role="student" />} />
                                {/* Legacy role-specific login URLs — kept as
                                    redirects so old bookmarks/emails don't 404.
                                    Iter34+ accepts any role at /login. */}
                                <Route path="/login/student" element={<Navigate to="/login" replace />} />
                                <Route path="/login/instructor" element={<Navigate to="/login" replace />} />
                                <Route path="/login/sme" element={<Navigate to="/login" replace />} />
                                <Route path="/login/staff" element={<Navigate to="/login" replace />} />
                                <Route path="/login/admin" element={<Navigate to="/login" replace />} />
                                <Route path="/account/2fa/setup" element={<TwoFactorSetup />} />
                                <Route path="/account/2fa/verify" element={<TwoFactorVerify />} />
                                <Route path="/register" element={<Register />} />
                                <Route path="/forgot-password" element={<ForgotPassword />} />
                                <Route path="/reset-password/:token" element={<ResetPassword />} />
                                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                                <Route path="/verify/:certId" element={<VerifyCertificate />} />

                                {/* Accuplacer English placement test — public */}
                                <Route path="/accuplacer" element={<AccuplacerLanding />} />
                                <Route path="/accuplacer/register" element={<AccuplacerRegister />} />
                                <Route path="/accuplacer/test" element={<AccuplacerTestLogin />} />
                                <Route path="/accuplacer/test/runner" element={<AccuplacerRunner />} />
                                <Route path="/accuplacer/test/window-open" element={<AccuplacerTestWindowOpen />} />

                                <Route
                                    path="/dashboard"
                                    element={
                                        <ProtectedRoute roles={["student", "instructor", "admin"]}>
                                            <StudentDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/learn/:programId"
                                    element={
                                        <ProtectedRoute roles={["student", "instructor", "admin"]}>
                                            <Learn />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/me/attendance"
                                    element={
                                        <ProtectedRoute roles={["student", "instructor", "admin"]}>
                                            <MyAttendance />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/me/profile"
                                    element={
                                        <ProtectedRoute roles={["student", "instructor", "admin", "sme"]}>
                                            <AccountProfile />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/certificate/:certId"
                                    element={
                                        <ProtectedRoute>
                                            <Certificate />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/account/change-password"
                                    element={
                                        <ProtectedRoute>
                                            <ChangePassword />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/instructor"
                                    element={
                                        <ProtectedRoute roles={["instructor", "admin"]}>
                                            <InstructorDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/instructor/programs/:programId"
                                    element={
                                        <ProtectedRoute roles={["instructor", "admin", "sme"]}>
                                            <InstructorProgramEditor />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/instructor/inbox"
                                    element={
                                        <ProtectedRoute roles={["instructor", "admin"]}>
                                            <InstructorInbox />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/instructor/students"
                                    element={
                                        <ProtectedRoute roles={["instructor", "admin"]}>
                                            <InstructorStudents />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/instructor/students/:studentId"
                                    element={
                                        <ProtectedRoute roles={["instructor", "admin"]}>
                                            <InstructorStudentDetail />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/sme"
                                    element={
                                        <ProtectedRoute roles={["sme", "admin"]}>
                                            <SMEDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/sme/programs/:programId"
                                    element={
                                        <ProtectedRoute roles={["sme", "admin"]}>
                                            <InstructorProgramEditor />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/admin"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/users"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminUsers />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/students"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminStudents />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/students/pending"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminPendingStudents />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/students/:studentId"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminStudentDetail />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/students/:studentId/attendance"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminStudentAttendance />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/integrity"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminIntegrity />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/accuplacer"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminAccuplacer />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/applications"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminApplications />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/instructors"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminInstructors />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/programs"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminPrograms />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/programs/schedules"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminProgramSchedules />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/assignments"
                                    element={
                                        <ProtectedRoute roles={["admin", "instructor", "sme"]}>
                                            <AdminAssignmentGrading />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/branding"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminBranding />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/attendance"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminAttendance />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>
                        </Routes>
                    </BrowserRouter>
                    <AiJobsTray />
                    </AiJobsProvider>
                </BrandingProvider>
                <Toaster position="top-right" />
            </AuthProvider>
        </div>
    );
}

export default App;
